import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../store/authStore';
import {
  MagicWand, Eye, LockSimple, DownloadSimple, X, ArrowRight,
  Spinner, CheckCircle, WarningCircle, Lightbulb, Sparkle, Lightning,
} from '@phosphor-icons/react';

const C = {
  bg:'#0a0a0f', surface:'#13131f', deep:'#0d0d18', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderHi:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', blue:'#3b82f6',
  grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#10b981', red:'#f87171', amber:'#fbbf24',
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Map a report issue to a fix category by keyword.
function classify(issue) {
  const s = `${issue.title || ''} ${issue.category || ''} ${issue.description || ''}`.toLowerCase();
  if (s.includes('meta description') || s.includes('description')) return 'meta';
  if (s.includes('title')) return 'title';
  if (s.includes('internal link') || s.includes('internal-link')) return 'link';
  return null;
}

export default function FixCenter({ report, analysisId }) {
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [modal, setModal] = useState(null);     // active action object
  const [upgrade, setUpgrade] = useState(false);
  const [howto, setHowto] = useState(null);

  useEffect(() => {
    apiClient.get('/audit/fix/quota').then(setQuota).catch(() => setQuota({ plan:'free', can_fix:false, unlimited:false, fixes_used:0, fixes_limit:0 }));
  }, []);

  const site = report?.url || '';
  const pageTitle = report?.meta_analysis?.title || '';
  const issues = report?.issues || [];
  const cats = new Set(issues.map(classify).filter(Boolean));

  // Build the catalog of fixable categories for this single-page report.
  const actions = [
    {
      key: 'meta', label: 'Meta description', points: 8, auto: true,
      icon: Lightbulb,
      reason: (report?.meta_analysis?.meta_description_length || 0) < 50
        ? 'Missing or too short on this page' : 'Can be optimized by AI',
      endpoint: '/audit/fix/meta-descriptions',
      body: (dry) => ({ site_url: site, page_urls: [site], dry_run: dry }),
      readValue: (r) => r.meta_description,
    },
    {
      key: 'title', label: 'Page title', points: 7, auto: true,
      icon: Sparkle,
      reason: 'Rewrite for clarity, length and keywords',
      endpoint: '/audit/fix/titles',
      body: (dry) => ({ site_url: site, rewrites: [{ url: site, current_title: pageTitle }], dry_run: dry }),
      readValue: (r) => r.new_title,
    },
    {
      key: 'link', label: 'Internal links', points: 5, auto: false,
      icon: ArrowRight,
      reason: 'Add contextual internal links (run a traffic audit for auto-fix)',
    },
  ];
  // Keep auto categories always; show "link" only if detected as an issue.
  const visible = actions.filter(a => a.auto || cats.has(a.key));

  const planLabel = () => {
    if (!quota) return '…';
    if (quota.unlimited) return '∞ Unlimited fixes · Agency';
    if (quota.plan === 'free') return 'Preview free · Upgrade to apply';
    return `${quota.fixes_used}/${quota.fixes_limit} fixes used this month`;
  };

  const isFree = quota?.plan === 'free';
  const canApply = quota && (quota.unlimited || quota.can_fix);

  return (
    <div style={{ marginBottom:16 }}>
      {/* Fix Center header */}
      <div style={{ background:C.grad, borderRadius:12, padding:'1px', marginBottom:12 }}>
        <div style={{ background:C.surface, borderRadius:11, padding:'16px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:11 }}>
            <div style={{ width:34, height:34, background:C.grad, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Lightning weight="fill" style={{ width:18, height:18, color:'#fff' }} />
            </div>
            <div>
              <h3 style={{ fontSize:15, fontWeight:800, color:C.text, margin:0, fontFamily:"'Satoshi', sans-serif" }}>Fix Center</h3>
              <p style={{ fontSize:12, color:C.muted, margin:'2px 0 0' }}>Turn issues into one-click fixes</p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20, fontSize:12, fontWeight:600,
              background: isFree ? 'rgba(251,191,36,0.12)' : 'rgba(16,185,129,0.12)',
              color: isFree ? C.amber : C.green, border:`1px solid ${isFree ? 'rgba(251,191,36,0.3)' : 'rgba(16,185,129,0.3)'}` }}>
              {isFree && <LockSimple weight="fill" style={{ width:12, height:12 }} />}
              {planLabel()}
            </span>
            {isFree && (
              <button onClick={() => navigate('/pricing')} style={btnGrad}>Upgrade</button>
            )}
          </div>
        </div>
      </div>

      {/* Category rows */}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {visible.map(a => (
          <div key={a.key} style={{ background:C.deep, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, minWidth:240 }}>
              <a.icon weight="fill" style={{ width:18, height:18, color:C.violet }} />
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{a.label}</div>
                <div style={{ fontSize:12, color:C.hint, marginTop:2 }}>
                  1 page · {a.reason} · <span style={{ color:C.green }}>+{a.points} pts</span>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              {a.auto ? (
                <>
                  <button onClick={() => setModal({ ...a, mode:'preview' })} style={btnGhost} title="Preview is free for all plans">
                    <Eye style={{ width:14, height:14 }} /> Preview
                  </button>
                  {isFree ? (
                    <button onClick={() => setUpgrade(true)} style={btnLocked} title="Upgrade to Pro to fix automatically">
                      <LockSimple weight="fill" style={{ width:14, height:14 }} /> Fix with AI
                    </button>
                  ) : (
                    <button onClick={() => setModal({ ...a, mode:'apply' })} style={btnGrad} disabled={!canApply}
                      title={canApply ? '' : 'Monthly limit reached'}>
                      <MagicWand weight="fill" style={{ width:14, height:14 }} /> Fix with AI
                    </button>
                  )}
                </>
              ) : (
                <button onClick={() => setHowto(a)} style={btnGhost}>
                  <Lightbulb style={{ width:14, height:14 }} /> How to fix?
                </button>
              )}
              <button onClick={() => exportPatch(a, site, pageTitle)} style={btnIcon} title="Export patch (JSON)">
                <DownloadSimple style={{ width:15, height:15 }} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <FixModal action={modal} report={report} analysisId={analysisId} canApply={canApply} isFree={isFree}
          onClose={() => setModal(null)}
          onNeedUpgrade={() => { setModal(null); setUpgrade(true); }}
          onApplied={(summary) => {
            setModal(null);
            navigate(`/report/${analysisId}/after`, { state: { scoreBefore: report.overall_score, url: site, fixes: summary } });
          }} />
      )}
      {upgrade && <UpgradeModal onClose={() => setUpgrade(false)} onUpgrade={() => navigate('/pricing')} />}
      {howto && <HowToModal action={howto} report={report} onClose={() => setHowto(null)} />}
    </div>
  );
}

function FixModal({ action, report, canApply, isFree, onClose, onApplied, onNeedUpgrade }) {
  const [phase, setPhase] = useState('idle');   // idle|running|preview|done|error
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const run = async (dryRun) => {
    if (!dryRun && isFree) { onNeedUpgrade(); return; }
    setPhase('running'); setErr(''); setResults([]);
    setMsg(dryRun ? 'Generating preview…' : 'Applying fixes to your site…');
    try {
      let job = await apiClient.post(action.endpoint, action.body(dryRun));
      if (job && job.status === 'queued' && job.fix_job_id) {
        for (let i = 0; i < 40; i++) {
          await sleep(2000);
          job = await apiClient.get(`/audit/fix/status/${job.fix_job_id}`);
          setMsg(`Working… ${job.progress || 0}%`);
          if (['completed', 'failed'].includes(job.status)) break;
        }
      }
      const res = job.results || [];
      setResults(res);
      setMsg(job.message || `${job.applied ?? res.filter(r => r.success).length} item(s) processed.`);
      if (dryRun) {
        setPhase('preview');
      } else {
        const applied = job.applied ?? res.filter(r => r.success).length;
        setPhase('done');
        if (applied > 0) {
          onApplied([{ type: action.key, count: applied, label: action.label, points: action.points }]);
        }
      }
    } catch (e) {
      if (e.status === 403 && (e.detail?.error === 'upgrade_required' || e.detail?.error === 'quota_exceeded')) {
        onNeedUpgrade(); return;
      }
      setErr(e.message || 'Fix failed'); setPhase('error');
    }
  };

  useEffect(() => { run(true); /* auto-preview on open */ }, []); // eslint-disable-line

  return (
    <Overlay onClose={onClose}>
      <div style={modalBox}>
        <div style={modalHead}>
          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
            <action.icon weight="fill" style={{ width:18, height:18, color:C.violet }} />
            <h3 style={{ fontSize:16, fontWeight:800, color:C.text, margin:0, fontFamily:"'Satoshi', sans-serif" }}>{action.label}</h3>
          </div>
          <button onClick={onClose} style={btnIcon}><X style={{ width:16, height:16 }} /></button>
        </div>

        <p style={{ fontSize:13, color:C.muted, margin:'0 0 14px' }}>
          We're about to fix <b style={{ color:C.text }}>{action.label.toLowerCase()}</b> on <b style={{ color:C.text }}>{new URL(report.url).hostname}</b>.
          {' '}Preview is free; applying updates your site directly.
        </p>

        {(phase === 'running') && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'18px', background:C.deep, borderRadius:9, marginBottom:14 }}>
            <Spinner className="animate-spin" style={{ width:18, height:18, color:C.violet }} />
            <span style={{ fontSize:13, color:C.muted }}>{msg}</span>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px', background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.25)', borderRadius:9, marginBottom:14 }}>
            <WarningCircle weight="fill" style={{ width:18, height:18, color:C.red }} />
            <span style={{ fontSize:13, color:C.red }}>{err}</span>
          </div>
        )}

        {(phase === 'preview' || phase === 'done') && (
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:8 }}>
              {phase === 'done'
                ? <><CheckCircle weight="fill" style={{ width:16, height:16, color:C.green }} /><span style={{ fontSize:13, fontWeight:700, color:C.green }}>Applied</span></>
                : <><Eye weight="fill" style={{ width:16, height:16, color:C.blue }} /><span style={{ fontSize:13, fontWeight:700, color:C.blue }}>Preview (no changes made)</span></>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:260, overflowY:'auto' }}>
              {results.length === 0 && <p style={{ fontSize:13, color:C.hint, margin:0 }}>{msg}</p>}
              {results.map((r, i) => (
                <div key={i} style={{ background:C.deep, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:11, color:C.hint, fontFamily:"'JetBrains Mono', monospace", marginBottom:5, wordBreak:'break-all' }}>{r.url}</div>
                  <div style={{ fontSize:13, color:C.text }}>{action.readValue ? (action.readValue(r) || r.would_insert || '—') : (r.meta_description || r.new_title || '—')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:9 }}>
          {phase === 'done' ? (
            <button onClick={onClose} style={btnGrad}>Done</button>
          ) : (
            <>
              <button onClick={() => run(true)} style={btnGhost} disabled={phase === 'running'}>
                <Eye style={{ width:14, height:14 }} /> {phase === 'preview' ? 'Refresh preview' : 'Preview'}
              </button>
              {isFree ? (
                <button onClick={onNeedUpgrade} style={btnLocked}>
                  <LockSimple weight="fill" style={{ width:14, height:14 }} /> Apply (Upgrade)
                </button>
              ) : (
                <button onClick={() => run(false)} style={btnGrad} disabled={phase === 'running' || !canApply}>
                  <MagicWand weight="fill" style={{ width:14, height:14 }} /> Apply now
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
}

function UpgradeModal({ onClose, onUpgrade }) {
  return (
    <Overlay onClose={onClose}>
      <div style={{ ...modalBox, maxWidth:420, textAlign:'center' }}>
        <div style={{ width:52, height:52, background:C.grad, borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', margin:'4px auto 14px' }}>
          <MagicWand weight="fill" style={{ width:26, height:26, color:'#fff' }} />
        </div>
        <h3 style={{ fontSize:19, fontWeight:900, color:C.text, margin:'0 0 8px', fontFamily:"'Satoshi', sans-serif" }}>Upgrade to Pro to fix automatically</h3>
        <p style={{ fontSize:13.5, color:C.muted, margin:'0 0 18px', lineHeight:1.6 }}>
          Previewing AI fixes is free. To apply them directly to your site — meta descriptions, titles and internal links — upgrade to a paid plan.
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:8, textAlign:'left', background:C.deep, borderRadius:10, padding:'14px 16px', marginBottom:18 }}>
          {['Apply unlimited AI fixes', 'Auto-push to WordPress', 'Before / After tracking', 'Priority support'].map(f => (
            <div key={f} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.text }}>
              <CheckCircle weight="fill" style={{ width:15, height:15, color:C.green }} /> {f}
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:9 }}>
          <button onClick={onClose} style={{ ...btnGhost, flex:1, justifyContent:'center' }}>Maybe later</button>
          <button onClick={onUpgrade} style={{ ...btnGrad, flex:1, justifyContent:'center' }}>See plans <ArrowRight weight="bold" style={{ width:14, height:14 }} /></button>
        </div>
      </div>
    </Overlay>
  );
}

function HowToModal({ action, report, onClose }) {
  const related = (report.issues || []).filter(i => {
    const s = `${i.title} ${i.category} ${i.description}`.toLowerCase();
    return s.includes('link');
  });
  return (
    <Overlay onClose={onClose}>
      <div style={modalBox}>
        <div style={modalHead}>
          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
            <Lightbulb weight="fill" style={{ width:18, height:18, color:C.violet }} />
            <h3 style={{ fontSize:16, fontWeight:800, color:C.text, margin:0, fontFamily:"'Satoshi', sans-serif" }}>How to fix: {action.label}</h3>
          </div>
          <button onClick={onClose} style={btnIcon}><X style={{ width:16, height:16 }} /></button>
        </div>
        <ol style={{ margin:'0 0 6px', paddingLeft:18, color:C.muted, fontSize:13.5, lineHeight:1.8 }}>
          <li>Identify your top-performing pages on related topics.</li>
          <li>Add 2–3 contextual links from those pages to this URL using descriptive anchor text.</li>
          <li>Run a Traffic audit in SEO Dash to get AI link suggestions, then return here for one-click auto-fix.</li>
        </ol>
        {related.map((i, k) => i.fix_suggestion && (
          <p key={k} style={{ fontSize:13, color:C.muted, background:C.deep, padding:'10px 12px', borderRadius:8, margin:'10px 0 0' }}>{i.fix_suggestion}</p>
        ))}
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
          <button onClick={onClose} style={btnGrad}>Got it</button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(5,5,12,0.72)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:520 }}>{children}</div>
    </div>
  );
}

function exportPatch(action, site, pageTitle) {
  const patch = {
    site_url: site,
    fix_type: action.key,
    generated_at: new Date().toISOString(),
    targets: [{ url: site, current_title: pageTitle }],
    note: 'Apply these changes via SEO Dash Fix Center or your CMS.',
  };
  const blob = new Blob([JSON.stringify(patch, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `seodash-fix-${action.key}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const btnBase = { display:'inline-flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:8, fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', border:'none' };
const btnGrad = { ...btnBase, background:C.grad, color:'#fff', boxShadow:'0 2px 12px rgba(124,58,237,0.3)' };
const btnGhost = { ...btnBase, background:'transparent', color:C.muted, border:`1px solid ${C.border}` };
const btnLocked = { ...btnBase, background:'rgba(251,191,36,0.1)', color:C.amber, border:'1px solid rgba(251,191,36,0.3)' };
const btnIcon = { ...btnBase, padding:8, background:'transparent', color:C.hint, border:`1px solid ${C.border}` };
const modalBox = { background:C.surface, border:`1px solid ${C.borderHi}`, borderRadius:14, padding:22, boxShadow:'0 20px 60px rgba(0,0,0,0.5)' };
const modalHead = { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 };
